import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription }                 from 'rxjs';
import { TranslateService }             from '@ngx-translate/core';

import { IntelligenceFormatService } from '../intelligence.format.service';
import { IntelligenceService }       from '../intelligence.service';
import { IntelligenceSummaryModel }  from '../typings/intelligence.summary';

@Component({
  selector   : 'wor-intelligence-overview-tab',
  template: require('./intelligence.overview-tab.component.html')
})
export class OverviewTabComponent implements OnInit, OnDestroy {
  error = false;
  loading = true;

  summary : IntelligenceSummaryModel = null;

  private loadGeneration = 0;
  private subscription : Subscription;

  constructor (
    private intelligenceFormatService: IntelligenceFormatService,
    public intelligenceService       : IntelligenceService,
    private translateService         : TranslateService
  ) {}

  ngOnInit () : void {
    this.load();

    this.subscription = this.intelligenceService.rangeChanged.subscribe(() => this.load());
  }

  ngOnDestroy () : void {
    this.subscription.unsubscribe();
  }

  count ( key : string ) : string {
    return this.summary && this.summary.kpis[ key ]
      ? this.intelligenceFormatService.count(this.summary.kpis[ key ].value)
      : '';
  }

  delta ( key : string ) : number {
    return this.summary && this.summary.kpis[ key ]
      ? this.summary.kpis[ key ].delta_pct
      : null;
  }

  grossMargin () : string {
    if ( !this.summary ) {
      return '';
    }

    const grossProfit = this.summary.kpis[ 'gross_profit' ];
    const revenue = this.summary.kpis[ 'revenue' ];

    return !grossProfit || !revenue || !revenue.value
      ? this.intelligenceFormatService.percent(null)
      : this.intelligenceFormatService.percent(grossProfit.value / revenue.value * 100);
  }

  grossMarginBand () : string {
    if ( !this.summary ) {
      return null;
    }

    const grossProfit = this.summary.kpis[ 'gross_profit' ];
    const revenue = this.summary.kpis[ 'revenue' ];

    if ( !grossProfit || !revenue || !revenue.value ) {
      return null;
    }

    const margin = grossProfit.value / revenue.value * 100;

    return margin >= 55
      ? 'good'
      : margin >= 45
        ? 'warn'
        : 'bad';
  }

  grossMarginDelta () : number {
    if ( !this.summary ) {
      return null;
    }

    const grossProfit = this.summary.kpis[ 'gross_profit' ];
    const revenue = this.summary.kpis[ 'revenue' ];

    if ( !grossProfit || !revenue || !revenue.value || !revenue.prior ) {
      return null;
    }

    return ( grossProfit.value / revenue.value * 100 ) - ( grossProfit.prior / revenue.prior * 100 );
  }

  load () : void {
    const generation = ++this.loadGeneration;

    this.error = false;
    this.loading = true;

    this.intelligenceService.getSummary()
    .then(( data : IntelligenceSummaryModel ) => {
      if ( generation !== this.loadGeneration ) {
        return;
      }

      if ( !data || !data.kpis ) {
        this.error = true;
        this.summary = null;

        return;
      }

      this.summary = data;
    })
    .catch(() => {
      if ( generation === this.loadGeneration ) {
        this.error = true;
      }
    })
    .finally(() => {
      if ( generation === this.loadGeneration ) {
        this.loading = false;
      }
    });
  }

  money ( key : string ) : string {
    return this.summary && this.summary.kpis[ key ]
      ? this.intelligenceFormatService.money(this.summary.kpis[ key ].value)
      : '';
  }

  receivables () : string {
    return this.summary && this.summary.receivables
      ? this.intelligenceFormatService.money(this.summary.receivables.outstanding)
      : '';
  }

  wipCaption () : string {
    if ( !this.summary || !this.summary.work_in_progress ) {
      return '';
    }

    const wip = this.summary.work_in_progress;
    const jobs = this.translateService.instant('INTELLIGENCE_SPACE.COMMON.JOBS');
    const grossProfit = this.translateService.instant('INTELLIGENCE_SPACE.COMMON.GROSS_PROFIT');

    return `${ wip.job_count } ${ jobs } · ${ this.intelligenceFormatService.money(wip.estimated_gross_profit) } ${ grossProfit }`;
  }

  wipValue () : string {
    return this.summary && this.summary.work_in_progress
      ? this.intelligenceFormatService.money(this.summary.work_in_progress.value_ex_tax)
      : '';
  }
}
